import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import MobileAppLayout from "../../layouts/MobileAppLayout";
import { GameCard, GameHero, GameIcon, Skeleton } from "../../components/game";
import { resolveMediaUrl } from "../student/quiz/media";
import api from "../../services/api";

type TeamProfile = {
  id: string;
  name: string;
  sport: string | null;
  age_group: string | null;
  club: { id: string; name: string; logo_url: string | null } | null;
  coach: { id: string; full_name: string | null; avatar_url: string | null } | null;
  members: { id: string; full_name: string | null; avatar_url: string | null; points: number }[];
};

function Avatar({ url, name, className = "" }: { url: string | null; name: string | null; className?: string }) {
  if (url) return <img src={resolveMediaUrl(url) || undefined} alt={name || ""} className={`object-cover ${className}`} />;
  return (
    <div className={`bg-gradient-to-br from-indigo-500 to-blue-600 grid place-items-center text-white font-extrabold ${className}`}>
      {(name || "?").charAt(0).toUpperCase()}
    </div>
  );
}

export default function AppTeamProfile() {
  const { t } = useTranslation();
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const [team, setTeam] = useState<TeamProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!teamId) return;
    setLoading(true);
    api
      .get<TeamProfile>(`/profiles/team/${teamId}`)
      .then((r) => setTeam(r.data))
      .catch((e) => setError(e?.response?.data?.error || t("team.unavailable", "Echipă indisponibilă")))
      .finally(() => setLoading(false));
  }, [teamId]);

  const medal = (i: number) => (i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : null);

  return (
    <MobileAppLayout>
      <div className="px-4 py-6 space-y-5">
        <GameHero flow className="-mx-4 -mt-6 rounded-br-3xl px-4 pt-3 pb-5">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-white/80 text-sm font-semibold mb-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.4} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            {t("team.back", "Înapoi")}
          </button>
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-white/15 grid place-items-center shrink-0 ring-2 ring-white/20">
              <GameIcon name="medal" className="w-7 h-7 text-white/90" />
            </div>
            <div className="min-w-0">
              <p className="text-white/70 text-[11px] font-bold uppercase tracking-wide">{t("team.teamLabel", "Echipă")}</p>
              <h1 className="text-xl font-extrabold truncate">{team?.name || (loading ? "…" : t("team.teamLabel", "Echipă"))}</h1>
              <p className="text-white/70 text-[13px]">
                {[team?.sport, team?.age_group].filter(Boolean).join(" · ")}
              </p>
            </div>
          </div>
        </GameHero>

        {loading ? (
          <div className="space-y-3 animate-fadeIn">
            <Skeleton className="h-16 rounded-2xl" />
            <Skeleton className="h-24 rounded-2xl" />
          </div>
        ) : error || !team ? (
          <GameCard className="p-8 text-center text-slate-500">{error || t("team.loadError", "Nu s-au putut încărca datele")}</GameCard>
        ) : (
          <>
            {/* Club + coach */}
            <div className="grid grid-cols-1 gap-3">
              {team.club && (
                <Link to={`/app/club/${team.club.id}`}>
                  <GameCard className="p-3.5 flex items-center gap-3 active:translate-y-[1px] transition">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 grid place-items-center overflow-hidden shrink-0">
                      {team.club.logo_url ? (
                        <img src={resolveMediaUrl(team.club.logo_url) || undefined} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <GameIcon name="trophy" className="w-5 h-5 text-slate-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] text-slate-400 font-bold uppercase">{t("team.clubLabel", "Club")}</p>
                      <p className="font-bold text-slate-800 truncate">{team.club.name}</p>
                    </div>
                    <svg className="w-5 h-5 text-slate-400 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </GameCard>
                </Link>
              )}
              {team.coach && (
                <GameCard className="p-3.5 flex items-center gap-3">
                  <Avatar url={team.coach.avatar_url} name={team.coach.full_name} className="w-10 h-10 rounded-full shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-slate-400 font-bold uppercase">{t("team.coach", "Antrenor")}</p>
                    <p className="font-bold text-slate-800 truncate">{team.coach.full_name || "—"}</p>
                  </div>
                </GameCard>
              )}
            </div>

            {/* Roster */}
            <div>
              <h2 className="text-sm font-bold text-slate-700 mb-2">
                {t("team.teammates", "Colegi de echipă")} {team.members.length > 0 && <span className="text-slate-400">({team.members.length})</span>}
              </h2>
              {team.members.length === 0 ? (
                <GameCard className="p-6 text-center text-sm text-slate-500">{t("team.noTeammates", "Niciun coleg încă.")}</GameCard>
              ) : (
                <div className="space-y-2">
                  {team.members.map((m, i) => (
                    <GameCard
                      key={m.id}
                      onClick={() => navigate(`/app/profile/${m.id}`)}
                      className="p-3 flex items-center gap-3 cursor-pointer active:translate-y-[1px] transition"
                    >
                      <span className="w-6 text-center font-extrabold text-slate-400 tabular-nums shrink-0">
                        {medal(i) || i + 1}
                      </span>
                      <Avatar url={m.avatar_url} name={m.full_name} className="w-10 h-10 rounded-full shrink-0" />
                      <p className="flex-1 min-w-0 font-bold text-slate-800 truncate">{m.full_name || t("team.athleteFallback", "Sportiv")}</p>
                      <span className="inline-flex items-center gap-1 text-amber-600 font-extrabold text-sm tabular-nums shrink-0">
                        <GameIcon name="star" className="w-4 h-4" />
                        {m.points}
                      </span>
                    </GameCard>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </MobileAppLayout>
  );
}
